import type { ReactNode } from "react";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNameFormatter } from "src/features/graph/util/NameFormatter";
import { useMediasByIds } from "src/features/gallery/useMediaById";
import Spinner from "src/common/components/Loader/Spinner";

export interface WarningEntity {
  name?: string;
  projectName?: string;
  username?: string;
  apiId: string;
  fieldName?: string;
  projectMedias: Array<string | number>;
}

export interface IEntitiesToDisplayWarning {
  users: WarningEntity[];
  projects: WarningEntity[];
  organizations: WarningEntity[];
}
export type MediaModuleWarningSeverity = "danger" | "info";

interface IModuleWarning {
  entitiesToDisplayWarning: IEntitiesToDisplayWarning;
  severity?: MediaModuleWarningSeverity;
}

const WarningIcon = ({ severity }: { severity: MediaModuleWarningSeverity }) =>
  severity === "info" ? (
    <InformationCircleIcon className="stroke-gray-500 h-5 w-5 mr-2" />
  ) : (
    <ExclamationCircleIcon className="stroke-red-500 h-5 w-5 mr-2" />
  );

const Dropdown = ({
  title,
  isOpen,
  toggleDropdown,
  children,
  severity,
}: {
  title: string;
  isOpen: boolean;
  toggleDropdown: () => void;
  children: ReactNode;
  severity: MediaModuleWarningSeverity;
}) => (
  <div className="dropdown border-2 border-gray-300">
    <div className="dropdown-header" onClick={toggleDropdown}>
      <WarningIcon severity={severity} />
      {title}
      {isOpen ? (
        <ChevronDownIcon className="stroke-green-500 ml-2 h-4 w-4" />
      ) : (
        <ChevronRightIcon className="stroke-green-500 ml-2 h-4 w-4" />
      )}
    </div>
    {isOpen && (
      <div className="dropdown-body open py-2 grid grid-flow-col h-auto border-t-2">{children}</div>
    )}
  </div>
);

export const MediaModules = ({ entitiesToDisplayWarning, severity = "danger" }: IModuleWarning) => {
  const { t } = useTranslation();
  const { getSameFormattedModelNameThanUnity } = useNameFormatter();
  const [isOpen, setOpen] = useState<number | null>(null);

  const relevantMediaIds = useMemo(() => {
    const ids = new Set<number>();
    const collect = (entities: WarningEntity[]) => {
      entities.forEach((entity) =>
        entity.projectMedias.forEach((item) => {
          const id = Number(item);
          if (Number.isFinite(id)) ids.add(id);
        }),
      );
    };
    collect(entitiesToDisplayWarning.projects);
    collect(entitiesToDisplayWarning.users);
    collect(entitiesToDisplayWarning.organizations);
    return Array.from(ids);
  }, [entitiesToDisplayWarning]);

  const mediasById = useMediasByIds(relevantMediaIds);

  const toggleDropdown = (index: number) => setOpen(isOpen === index ? null : index);

  const renderMedias = ({
    items,
    usedAsWhat,
  }: {
    items: Array<string | number>;
    usedAsWhat: string;
  }) => {
    return items.map((item) => {
      const entry = mediasById[Number(item)];
      const media = entry?.media;
      const isLoading = !media && (entry === undefined || entry.status === "loading");
      return (
        <div className="ml-3" key={item} id={String(item)}>
          <h6>
            {isLoading
              ? " "
              : `${
                  media
                    ? `${t("pages.gallery.deleteMedia.mediaNamed")} "${media.name}"`
                    : `${t("pages.gallery.deleteMedia.mediaWithId")} "${item}"`
                } ${t("pages.gallery.deleteMedia.usedAs")} ${usedAsWhat}`}
          </h6>
        </div>
      );
    });
  };

  const renderSection = ({
    entities,
    contextText,
    withDropdown = false,
  }: {
    entities: WarningEntity[];
    contextText: { header: string; usedAsWhat?: string };
    withDropdown?: boolean;
  }) =>
    entities.length >= 1 && (
      <>
        <div className="mx-5 mt-4">
          <h4 className="font-semibold text-xl">{contextText.header}</h4>
        </div>
        <div className={`overflow-y-auto ${entities.length < 3 ? "" : "h-72"}`}>
          {entities.map((entity, index) => {
            return withDropdown ? (
              <Dropdown
                key={`${entity.name}_${index}`}
                title={entity.projectName ?? ""}
                isOpen={isOpen === index}
                toggleDropdown={() => toggleDropdown(index)}
                severity={severity}
              >
                {renderMedias({
                  items: entity.projectMedias,
                  usedAsWhat: !contextText.usedAsWhat
                    ? entity.apiId.includes("content")
                      ? `${getSameFormattedModelNameThanUnity(entity.apiId)}`
                      : `${getSameFormattedModelNameThanUnity(
                          `${entity.apiId}.${entity.fieldName}`,
                        )}`
                    : contextText.usedAsWhat,
                })}
              </Dropdown>
            ) : (
              <div
                key={`${entity.username}_${index}`}
                className="dropdown border-2 border-gray-300"
              >
                <div className="dropdown-header">
                  <WarningIcon severity={severity} />
                  {renderMedias({
                    items: entity.projectMedias,
                    usedAsWhat: !contextText.usedAsWhat
                      ? entity.apiId.includes("content")
                        ? `${getSameFormattedModelNameThanUnity(entity.apiId)}`
                        : `${getSameFormattedModelNameThanUnity(
                            `${entity.apiId}.${entity.fieldName}`,
                          )}`
                      : contextText.usedAsWhat,
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );

  const projectContextText = {
    header:
      entitiesToDisplayWarning.projects.length > 1
        ? t("pages.gallery.deleteMedia.yourModules")
        : t("pages.gallery.deleteMedia.yourModule"),
  };

  const userContextText = {
    header: t("pages.gallery.deleteMedia.yourUser"),
    usedAsWhat: t("pages.gallery.deleteMedia.profilePicture"),
  };

  const organizationContextText = {
    header: t("pages.gallery.deleteMedia.yourOrganization"),
    usedAsWhat: t("pages.gallery.deleteMedia.organizationLogo"),
  };

  if (!entitiesToDisplayWarning || Object.keys(entitiesToDisplayWarning).length === 0) {
    return <Spinner />;
  }

  return (
    <div className="justify-center">
      {renderSection({
        entities: entitiesToDisplayWarning.projects,
        contextText: projectContextText,
        withDropdown: true,
      })}
      {renderSection({
        entities: entitiesToDisplayWarning.users,
        contextText: userContextText,
      })}
      {renderSection({
        entities: entitiesToDisplayWarning.organizations,
        contextText: organizationContextText,
      })}
    </div>
  );
};
